import React, { useRef, useState, useEffect, useCallback } from "react";
import styled from "styled-components";

const PROMPT = "C:\\>";


export default function Cmd({ onClose, isFocus }) {
  const [lines, setLines]   = useState([
    "Microsoft Windows XP [Version 5.1.2600]",
    "(C) Copyright 1985-2001 Microsoft Corp.",
    "",                                           // blank line
  ]);
  const [input, setInput]   = useState("");
  const scrollRef           = useRef(null);
  const inputRef            = useRef(null);

  const focusInput = () =>  requestAnimationFrame(() => {
    if (inputRef.current) {
        inputRef.current.focus()
    }
  });

  // Always scroll to bottom after a new line is printed
  useEffect(() => {
    if (scrollRef.current) {
        scrollRef.current.scrollTo(0, scrollRef.current.scrollHeight);
    }
  }, [lines]);

  useEffect(() => {
    if (isFocus) focusInput();
  }, [isFocus]);

  const append = useCallback(
    (...xs) => setLines(prev => [...prev, ...xs.flat()]),
    []
  );

  const cmdCLS = () => setLines([]);
  const cmdWHOAMI = () => append(
    '‎',
    '     Mihael Miseric',
    '‎',
    '> Role:',
    '     ​​​​• Information Security Specialist & Penetration Tester, specializing in web application security.',
    '‎',
    '> Experience:',
    '     • Hands-on penetration testing across web apps, APIs, thick clients, internal/external networks, source code review.',
    '‎',
    '> Skills:',
    '     • Proficient with BurpSuite, Nmap, Wireshark, Metasploit, Nessus',
    '     • Skilled in automating PoCs & exploits with Python and JavaScript.',
    '‎',
    '> Certifications:',
    '     • OSEP (07/2025)',
    '     • OSWE (03/2025)',
    '     • OSCP+ (11/2024)',
    '     • OSCP (08/2024)',
    '     • BSCP (07/2024)',
    '     • CEH (09/2023)',
    '‎',
    '> Achievements:',
    '     • Croatian National Team @ ECSC 2023 & 2024',
    '     • EC-Council Bug Bounty Hall of Fame',
    '     • 1st place CEH Global Competition (Sept & Oct 2023)',
    '‎',
    '> Currently:',
    '     • Progressing through OSED (EXP-301) → aiming for OSCE3 by end of 2025.',
    '‎'
  );
  const cmdHELP = () =>
    append(
      'Built-in commands:',
      '     cls               - Clear the screen',
      '     exit              - Close Command Prompt',
      '     help | ?          - Display this help',
      '     nslookup <host>',
      '     WHOAMI            - Display user name \n\n'
    );

  // --- PING (client-side simulation) -------
  const simulatePing = async host => {
    append(`\nPinging ${host} with 32 bytes of data:\n`);
    const echo = i =>
      new Promise(res => {
        const img = new Image();
        const start = Date.now();
        const finish = ok => {
          const ms = Date.now() - start;
          res(
            ok
              ? `Reply from ${host}: bytes=32 time=${ms}ms TTL=${Math.floor(
                  40 + Math.random() * 40
                )}`
              : `Request timed out.`
          );
        };
        img.onload = () => finish(true);
        img.onerror = () => finish(false);
        // bust the cache so every echo fires
        img.src = `https://${host}/?${Math.random()}`;
        setTimeout(() => finish(false), 1500);
      });

    const replies = [];
    for (let i = 0; i < 4; i++) replies.push(await echo(i));
    const success = replies.filter(r => r.startsWith('Reply')).length;
    append(replies, `\nPing statistics for ${host}:`, `    Packets: Sent = 4, Received = ${success}, Lost = ${4 - success} (${((4 - success) / 4) * 100}% loss)`, '');
  };

  // --- NSLOOKUP (Google DNS over HTTPS) ----
  const nslookup = async host => {
    try {
      const r = await fetch(
        `https://dns.google/resolve?name=${encodeURIComponent(host)}`
      );
      const json = await r.json();

      const answers = (json.Answer || []).filter(a => a.type === 1).map(a => a.data);

      append(
        '‎',
        'Server:  dns.google',
        'Address:  8.8.8.8',
        '‎',
        answers.length ? 'Non-authoritative answer:' : '*** No answer found ***',
        ...answers.map((a, i) => `Name:    ${host}\nAddresses${i ? ' ' : ':'} ${a}`),
        '‎'
      );
    } catch {
      append('DNS request failed.\n');
    }
  };

  const run = async raw => {
    const trimmed = raw.trim();
    if (!trimmed) return;

    const [cmd, ...args] = trimmed.split(/\s+/);

    const cmdLower = cmd ? cmd.toLowerCase() : '';
    switch (cmdLower) {
      case 'cls':
        cmdCLS();
        break;
      case 'whoami':
        cmdWHOAMI();
        break;
      case 'help':
      case '?':
        cmdHELP();
        break;
      case 'ping':
        if (!args[0]) append('Ping request could not find host.');
        else await simulatePing(args[0]);
        break;
      case 'nslookup':
        if (!args[0]) append('*** nslookup needs a domain ***\n');
        else await nslookup(args[0]);
        break;
      case 'exit':
        onClose(); // close the window
        return;    // don’t print prompt again
      default:
        append(`'${cmd}' is not recognized as an internal or external command,\noperable program or batch file.`);
    }
  };

  function handleKeyDown(e) {
    if (e.key !== "Enter") return;
    e.preventDefault();

    const current = input;
    setLines(prev => [...prev, `${PROMPT} ${current}`]);
    setInput("");
    run(current);
  }

  return (
    <Console
       ref={scrollRef}
       tabIndex={-1}
       onMouseDown={e => {
          e.preventDefault();
          focusInput();
       }}
    >
      {lines.map((l, i) => (
        <div key={i} className="line">{l}</div>
      ))}

      {/* active prompt */}
      <div className="line">
        {PROMPT}&nbsp;
        <Input
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          style={{
            cursor: "default"
          }}
          autoFocus
        />
      </div>
    </Console>
  );
}

/* ----------------------------  styling  ----------------------------- */
/* Keeps the classic black-on-white XP terminal look without XP.css */
const Console = styled.div`
  background: #000;
  color: #fff;
  font-family: "Lucida Console", monospace;
  font-size: 13px;
  line-height: 18px;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding: 4px;
  box-sizing: border-box;
  cursor: default;
`;

const Input = styled.input`
  background: transparent;
  border: none;
  color: inherit;
  font: inherit;
  outline: none;
  width: 80%;
`;